/**
 * ContactForm Component
 *
 * Contact form with subject selector.
 * Submits to Netlify function for email delivery.
 *
 * @param {object} props
 * @param {string} props.heading - Form heading
 * @param {array} props.subjects - Subject options from Prismic
 */

import React from "react";
import { Label } from "@radix-ui/react-label";
import Icon from "../Icon";
import FormSelect from "./FormSelect";
import LoadingSpinner from "../LoadingSpinner";
import * as styles from "./ContactForm.module.scss";

const DEFAULT_SUBJECTS = [
  "General Inquiry",
  "Partnerships",
  "Press",
  "Careers",
  "Other",
];

const ContactForm = ({ heading, subjects }) => {
  const subjectOptions = subjects?.length > 0 ? subjects : DEFAULT_SUBJECTS;

  const [subject, setSubject] = React.useState(subjectOptions[0]);
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [formStatus, setFormStatus] = React.useState("idle");

  const subjectId = React.useId();
  const nameId = React.useId();
  const emailId = React.useId();
  const phoneId = React.useId();
  const messageId = React.useId();

  async function handleSubmit(e) {
    e.preventDefault();
    setFormStatus("loading");

    const data = {
      subject,
      name,
      email,
      phone,
      message,
    };

    try {
      const response = await fetch("/.netlify/functions/sendContactEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setFormStatus("success");
        // Reset form
        setName("");
        setEmail("");
        setPhone("");
        setMessage("");
      } else {
        console.error("Email sending failed", await response.text());
        setFormStatus("error");
      }
    } catch (error) {
      console.error("Error occurred while sending email:", error);
      setFormStatus("error");
    }
  }

  return (
    <div className={styles.container}>
      {formStatus === "success" && (
        <div className={styles.successWrapper}>
          <p className="h3">Message sent!</p>
          <p className="text-body-sm">
            Thanks for reaching out! We&apos;ll get back to you as soon as
            possible.
          </p>
          <button
            type="button"
            className={styles.resetButton}
            onClick={() => setFormStatus("idle")}
          >
            Send another message
          </button>
        </div>
      )}

      {formStatus === "loading" && (
        <div className={styles.loadingWrapper}>
          <LoadingSpinner />
          <p>Sending...</p>
        </div>
      )}

      {formStatus === "error" && (
        <div className={styles.errorWrapper}>
          <p className="h3">Something went wrong</p>
          <p className="text-body-sm">
            Please try again later or email us directly.
          </p>
          <button
            type="button"
            className={styles.resetButton}
            onClick={() => setFormStatus("idle")}
          >
            Try again
          </button>
        </div>
      )}

      {formStatus === "idle" && (
        <div>
          {heading && <h3 className="h4">{heading}</h3>}
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <Label htmlFor={subjectId} className="visually-hidden">
                Subject
              </Label>
              <FormSelect
                availableValues={subjectOptions}
                value={subject}
                onValueChange={setSubject}
                id={subjectId}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor={nameId} className="visually-hidden">
                Name
              </label>
              <input
                type="text"
                placeholder="Your Name"
                value={name}
                id={nameId}
                name="name"
                required
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor={emailId} className="visually-hidden">
                Email Address
              </label>
              <input
                type="email"
                pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                placeholder="Your Email"
                value={email}
                id={emailId}
                name="email"
                required
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor={phoneId} className="visually-hidden">
                Phone Number
              </label>
              <input
                type="tel"
                pattern="[0-9\-\+\(\)\s]+"
                placeholder="Your Phone (optional)"
                value={phone}
                id={phoneId}
                name="phone"
                inputMode="tel"
                onChange={e => setPhone(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor={messageId} className="visually-hidden">
                Message
              </label>
              <textarea
                placeholder="Your Message"
                id={messageId}
                name="message"
                value={message}
                required
                onChange={e => setMessage(e.target.value)}
              />
            </div>

            <button type="submit" className={styles.submitButton}>
              Send Message <Icon name="send" size="1.2em" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
